import { useCallback, useEffect, useRef } from 'react';
import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { VirtuosoHandle } from 'react-virtuoso';
import { useSchedulePulseShallowStore } from '../../../../../../stores/schedule-pulse.store';

type UseOpenedRequestDetailsProps = {
  loadedRequests?: ScheduleRequest[];
};

export const useOpenedRequestDetails = ({ loadedRequests }: UseOpenedRequestDetailsProps) => {
  const { selectedRequestId, selectedRequestLocationId } = useSchedulePulseShallowStore(
    'selectedRequestId',
    'selectedRequestLocationId'
  );

  const virtuosoHandleRef = useRef<VirtuosoHandle | null>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastScrolledRequestKeyRef = useRef<string | null>(null);

  const attemptScrollToSelectedRequest = useCallback(() => {
    const virtuosoHandle = virtuosoHandleRef.current;

    if (!virtuosoHandle || !loadedRequests || !selectedRequestId) {
      if (!selectedRequestId) {
        lastScrolledRequestKeyRef.current = null;
      }
      return;
    }

    const requestKey = `${selectedRequestLocationId ?? ''}:${selectedRequestId}`;
    if (lastScrolledRequestKeyRef.current === requestKey) {
      return;
    }

    const requestIndex = loadedRequests.findIndex(
      (request) =>
        request.id === selectedRequestId &&
        (!selectedRequestLocationId || request.locationId === selectedRequestLocationId)
    );

    if (requestIndex === -1) {
      return;
    }

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      const currentHandle = virtuosoHandleRef.current;
      if (!currentHandle) {
        return;
      }

      currentHandle.scrollToIndex({ index: requestIndex, align: 'start', behavior: 'auto' });
      lastScrolledRequestKeyRef.current = requestKey;
      scrollTimeoutRef.current = null;
    }, 150);
  }, [loadedRequests, selectedRequestId, selectedRequestLocationId]);

  const virtuosoRef = useCallback(
    (handle: VirtuosoHandle | null) => {
      virtuosoHandleRef.current = handle;
      attemptScrollToSelectedRequest();
    },
    [attemptScrollToSelectedRequest]
  );

  useEffect(() => {
    attemptScrollToSelectedRequest();

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    };
  }, [attemptScrollToSelectedRequest]);

  return { virtuosoRef };
};
